/**
 * Student Planner API — the session student's personal items (R17–R21).
 *
 * POST   /v1/student-planner/personal-items             create (201, or 200 on a replay)
 * PATCH  /v1/student-planner/personal-items/:public_id  partial update (200)
 * DELETE /v1/student-planner/personal-items/:public_id  hard delete (204)
 *
 * Owner-only: the subject is always the session student at the active school.
 * The client generates `publicId`, so a retried create returns the item it
 * already made instead of a duplicate (KTD12).
 *
 * `date` is a school-local calendar day. `startTime` / `endTime` are school-local
 * wall-clock times on that day; the server stores them as UTC instants and the
 * item comes back with `startAt` / `endAt` (KTD4).
 */

import { z } from 'zod'

import { PlannerStatusSchema, type PlannerPersonalItem } from '../../schemas/entities/planner-item'

/** A school-local wall-clock time, 24-hour `HH:mm`. */
const LocalTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a time as HH:mm')

const TitleSchema = z.string().trim().min(1).max(120)
const NoteSchema = z.string().max(2000)

/** The schedule fields of a personal item, after defaults. */
export interface PlannerPersonalItemSchedule {
  date: string | null
  startTime: string | null
  endTime: string | null
}

/**
 * Why a schedule is invalid, or null when it is valid. A time range needs a
 * date and both ends, and must end after it starts on the same day — `HH:mm`
 * strings compare in time order, so "end ≤ start" also catches a range that
 * crosses midnight. The server re-checks the MERGED schedule on update, where
 * the body alone cannot tell.
 */
export function plannerPersonalItemScheduleIssue(schedule: PlannerPersonalItemSchedule): string | null {
  const { date, startTime, endTime } = schedule
  if ((startTime === null) !== (endTime === null)) return 'A time range needs both a start and an end time'
  if (startTime === null || endTime === null) return null
  if (date === null) return 'A time range needs a date'
  if (endTime <= startTime) return 'The end time must be after the start time on the same day'
  return null
}

export const CreatePlannerPersonalItemBodySchema = z
  .object({
    /** Client-generated id; a replay with the same id returns the same item. */
    publicId: z.uuid(),
    title: TitleSchema,
    /** Section publicId of a class the student is enrolled in. */
    sectionId: z.string().min(1).nullable().optional(),
    date: z.iso.date().nullable().optional(),
    startTime: LocalTimeSchema.nullable().optional(),
    endTime: LocalTimeSchema.nullable().optional(),
    note: NoteSchema.nullable().optional(),
    status: PlannerStatusSchema.default('todo')
  })
  .superRefine((body, ctx) => {
    const issue = plannerPersonalItemScheduleIssue({
      date: body.date ?? null,
      startTime: body.startTime ?? null,
      endTime: body.endTime ?? null
    })
    if (issue) ctx.addIssue({ code: 'custom', message: issue, path: ['endTime'] })
  })
export type CreatePlannerPersonalItemBody = z.infer<typeof CreatePlannerPersonalItemBodySchema>

/**
 * Partial update: an absent field keeps its stored value, `null` clears it.
 * Only what the body alone proves wrong is rejected here; the merged schedule
 * is checked by the server.
 */
export const UpdatePlannerPersonalItemBodySchema = z
  .object({
    title: TitleSchema.optional(),
    sectionId: z.string().min(1).nullable().optional(),
    date: z.iso.date().nullable().optional(),
    startTime: LocalTimeSchema.nullable().optional(),
    endTime: LocalTimeSchema.nullable().optional(),
    note: NoteSchema.nullable().optional(),
    status: PlannerStatusSchema.optional()
  })
  .superRefine((body, ctx) => {
    if (body.startTime && body.endTime && body.endTime <= body.startTime) {
      ctx.addIssue({
        code: 'custom',
        message: 'The end time must be after the start time on the same day',
        path: ['endTime']
      })
    }
    if (body.date === null && (body.startTime || body.endTime)) {
      ctx.addIssue({ code: 'custom', message: 'A time range needs a date', path: ['date'] })
    }
  })
export type UpdatePlannerPersonalItemBody = z.infer<typeof UpdatePlannerPersonalItemBodySchema>

export const PlannerPersonalItemParamsSchema = z.object({
  public_id: z.string().min(1)
})

/** Create and update both return the item as the feed carries it. */
export type PlannerPersonalItemResponse = PlannerPersonalItem
